import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLessons } from "../../contexts/LessonsContext";

const pad2 = (n) => String(n).padStart(2, "0");

const getLessonEndDateTime = (lesson) => {
  if (!lesson?.date) return null;
  const timePart = (lesson.endTime || lesson.startTime || "00:00")
    .toString()
    .substring(0, 5);
  return new Date(`${lesson.date}T${timePart}:00`);
};

const getLessonStartDateTime = (lesson) => {
  if (!lesson?.date) return null;
  const timePart = (lesson.startTime || "00:00").toString().substring(0, 5);
  return new Date(`${lesson.date}T${timePart}:00`);
};

/* ── Shared sub-components ── */
const SortIcon = ({ field, sortConfig }) => {
  const isActive = sortConfig.key === field;
  const isAsc = isActive && sortConfig.direction === "asc";
  return (
    <svg
      className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${
        isActive ? "text-indigo-500" : "text-gray-300 group-hover:text-gray-400"
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {!isActive || isAsc ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M7 11l5-5m0 0l5 5m-5-5v12"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M17 13l-5 5m0 0l-5-5m5 5V6"
        />
      )}
    </svg>
  );
};

export default function LessonParticipantsReportModal({ isOpen, onClose, lesson }) {
  const { fetchLessonParticipants, getAvailableMeetings } = useLessons();

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isExporting, setIsExporting] = useState(false);

  const [meetingState, setMeetingState] = useState({
    checked: false,
    teacherStarted: false,
    active: false,
    hasAnySegment: false,
  });

  const lessonStartAt = useMemo(() => getLessonStartDateTime(lesson), [lesson]);
  const lessonEndAt = useMemo(() => getLessonEndDateTime(lesson), [lesson]);

  const isLessonFinished = useMemo(() => {
    return lessonEndAt ? lessonEndAt.getTime() <= Date.now() : false;
  }, [lessonEndAt]);

  const lessonTitle = lesson?.title || lesson?.subject || "מפגש";
  const courseTitle = lesson?.courseTitle || lesson?.course_name || "";

  useEffect(() => {
    if (!isOpen || !lesson?.id) return;

    let cancelled = false;
    let timerId = null;

    const loadMeetingState = async () => {
      try {
        const data = await getAvailableMeetings(lesson.id);

        const active =
          !!data &&
          Number(data.code) === 1 &&
          !!(data.joinUrl || data.hostUrl);

        const segmentCount = Number(data?.segmentCount || 0);

        const hasAnySegment =
          !!active ||
          !!data?.hasAnySegment ||
          segmentCount > 0 ||
          !!data?.lastSegmentId ||
          !!data?.lastMeetingId;

        if (!cancelled) {
          setMeetingState({
            checked: true,
            teacherStarted: hasAnySegment || active,
            active: !!active,
            hasAnySegment: !!hasAnySegment,
          });
        }
      } catch {
        if (!cancelled) {
          setMeetingState({
            checked: true,
            teacherStarted: false,
            active: false,
            hasAnySegment: false,
          });
        }
      }
    };

    loadMeetingState();

    if (!isLessonFinished) {
      timerId = window.setInterval(loadMeetingState, 5000);
    }

    return () => {
      cancelled = true;
      if (timerId) window.clearInterval(timerId);
    };
  }, [isOpen, lesson?.id, isLessonFinished, getAvailableMeetings]);

  const teacherStartedLesson = useMemo(() => {
    if (meetingState.teacherStarted) return true;

    // fallback אם כבר יש אינדיקציה על lesson עצמו
    if (lesson?.liveMeetingActive) return true;
    if (lesson?.teacherStarted) return true;
    if (lesson?.hasAnySegment) return true;

    return false;
  }, [meetingState.teacherStarted, lesson]);

  const showAttendanceNow = useMemo(() => {
    return isLessonFinished || teacherStartedLesson;
  }, [isLessonFinished, teacherStartedLesson]);

  const getAttendanceLabel = (participant) => {
    if (!showAttendanceNow) return "טרם התחיל";
    return participant?.attended === true ? "נוכח" : "נעדר";
  };

  const filteredParticipants = useMemo(() => {
    if (!searchTerm) return participants;
    const term = searchTerm.toLowerCase();
    return participants.filter((p) => {
      const name = (
        p.fullName || p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim()
      ).toLowerCase();

      return (
        name.includes(term) ||
        (p.email || "").toLowerCase().includes(term) ||
        (p.phone || p.mobile || "").toString().includes(searchTerm)
      );
    });
  }, [participants, searchTerm]);

  const sortedParticipants = useMemo(() => {
    if (!sortConfig.key) return filteredParticipants;

    return [...filteredParticipants].sort((a, b) => {
      const getVal = (p) => {
        if (sortConfig.key === "name") {
          return (
            p.fullName ||
            p.name ||
            `${p.firstName || ""} ${p.lastName || ""}`.trim()
          );
        }

        if (sortConfig.key === "attendance") {
          return getAttendanceLabel(p);
        }

        return p[sortConfig.key] ?? "";
      };

      const aVal = getVal(a);
      const bVal = getVal(b);

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredParticipants, sortConfig, showAttendanceNow]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleExport = () => {
    setIsExporting(true);

    setTimeout(() => {
      const csvContent = [
        ["שם", "אימייל", "טלפון", showAttendanceNow ? "נוכחות" : "סטטוס"],
        ...participants.map((p) => {
          const name =
            p.fullName ||
            p.name ||
            `${p.firstName || ""} ${p.lastName || ""}`.trim();

          return [
            name,
            p.email || "",
            p.phone || p.mobile || "",
            getAttendanceLabel(p),
          ];
        }),
      ]
        .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `participants_${lessonTitle}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setIsExporting(false);
    }, 300);
  };

  useEffect(() => {
    if (!isOpen || !lesson?.id) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const list = await fetchLessonParticipants(lesson.id);

        if (cancelled) return;

        setParticipants(
          Array.isArray(list)
            ? list.filter((p) => p.paid || p.hasPaid || p.paymentStatus === "PAID")
            : []
        );
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "שגיאה בטעינת רשימת המשתתפים");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, lesson?.id, fetchLessonParticipants]);

  useEffect(() => {
    if (!isOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const paidCount = participants.length;
  const presentCount = participants.filter((p) => p.attended === true).length;
  const absentCount = showAttendanceNow
    ? participants.filter((p) => p.attended !== true).length
    : 0;

  const formatDate = (str) => {
    if (!str) return "";
    const [y, m, d] = str.split("-").map(Number);
    return y && m && d ? `${pad2(d)}.${pad2(m)}.${y}` : str;
  };

  const startTime = (lesson?.startTime || "").toString().substring(0, 5);
  const endTime = (lesson?.endTime || "").toString().substring(0, 5);

  const thCls =
    "px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wide cursor-pointer group select-none";

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-gray-950/60 backdrop-blur-[3px] z-[120000]"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[120001] overflow-hidden" dir="rtl">
        <div className="flex h-full w-full items-start sm:items-center justify-center px-0 sm:px-4 pt-[max(12px,env(safe-area-inset-top))] sm:pt-0">
          <div
            className="
              w-full sm:max-w-3xl lg:max-w-4xl
              bg-white rounded-t-3xl sm:rounded-3xl
              shadow-2xl shadow-black/20
              flex flex-col overflow-hidden
              h-[calc(100svh-12px-env(safe-area-inset-top))]
              sm:h-[90vh]
              overscroll-contain
              will-change-transform
            "
          >
            <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <circle cx="8" cy="9" r="3.2" />
                    <circle cx="16" cy="9" r="3.2" />
                    <path d="M3.5 18.5C4.4 15.8 6.6 14 8.9 14c2.2 0 4.4 1.7 5.4 4.3" />
                    <path d="M14.5 18.5c.7-2.2 2.4-3.8 4.4-3.8 1.1 0 2.1.4 2.7 1" />
                  </svg>
                </div>

                <div className="min-w-0">
                  <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">
                    דוח משתתפים במפגש
                  </h2>
                  <p className="text-gray-500 text-xs mt-0.5 truncate">
                    {lessonTitle}
                    {courseTitle && ` · ${courseTitle}`} · {paidCount} משתתפים
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
                aria-label="סגור"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 2l10 10M12 2L2 12"
                    stroke="#6b7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-shrink-0 px-5 sm:px-7 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
              {lesson?.date && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" />
                  </svg>
                  {formatDate(lesson.date)}
                </span>
              )}

              {startTime && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
                  </svg>
                  {startTime}
                  {endTime && `–${endTime}`}
                </span>
              )}

              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                שילמו: {paidCount}
              </span>

              {showAttendanceNow ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    נוכחים: {presentCount}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                    נעדרים: {absentCount}
                  </span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                  טרם התחיל
                </span>
              )}
            </div>

            <div className="flex-shrink-0 px-5 sm:px-7 py-3.5 bg-white border-b border-gray-100 flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
              <div className="relative flex-1">
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>

                <input
                  type="text"
                  inputMode="text"
                  placeholder="חיפוש לפי שם, אימייל או טלפון..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 pr-9 pl-3 text-base sm:text-sm border border-gray-200 rounded-xl bg-gray-50 placeholder-gray-400 text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 focus:bg-white transition-all"
                />

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 2l8 8M10 2L2 10"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <button
                onClick={handleExport}
                disabled={isExporting || participants.length === 0}
                className="flex-shrink-0 flex items-center justify-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-100 hover:shadow-md hover:shadow-emerald-200 hover:-translate-y-px disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none transition-all duration-150"
              >
                {isExporting ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                )}
                ייצוא CSV
              </button>
            </div>

            {error && (
              <div className="flex-shrink-0 mx-5 sm:mx-7 mt-3 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600 font-medium">
                <svg className="w-4 h-4 flex-shrink-0 mt-px" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 7a1 1 0 012 0v4a1 1 0 01-2 0V7zm1 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7 py-4 scroll-pb-28">
              {loading && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <div className="w-10 h-10 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin mb-3" />
                  <p className="text-sm font-medium text-gray-500">טוען נתוני משתתפים…</p>
                </div>
              )}

              {!loading && sortedParticipants.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-300 border-2 border-dashed border-gray-200 rounded-xl">
                  <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="9" cy="8" r="4" strokeWidth="1.5" />
                    <path d="M3 20c0-3 2.5-5.5 5.5-5.5" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="17" cy="9" r="3" strokeWidth="1.5" />
                    <path d="M22 20c0-2.5-2-4.5-4.5-4.5" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <p className="text-sm font-medium text-gray-400">
                    {searchTerm ? "לא נמצאו תוצאות" : "אין עדיין משתתפים ששילמו"}
                  </p>

                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 font-medium"
                    >
                      נקה חיפוש
                    </button>
                  )}
                </div>
              )}

              {!loading && sortedParticipants.length > 0 && (
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className={`${thCls} w-10`}>#</th>

                        <th className={thCls} onClick={() => handleSort("name")}>
                          <div className="flex items-center gap-1.5">
                            שם תלמיד
                            <SortIcon field="name" sortConfig={sortConfig} />
                          </div>
                        </th>

                        <th className={`${thCls} hidden md:table-cell`} onClick={() => handleSort("phone")}>
                          <div className="flex items-center gap-1.5">
                            טלפון
                            <SortIcon field="phone" sortConfig={sortConfig} />
                          </div>
                        </th>

                        <th className={`${thCls} hidden sm:table-cell`} onClick={() => handleSort("email")}>
                          <div className="flex items-center gap-1.5">
                            אימייל
                            <SortIcon field="email" sortConfig={sortConfig} />
                          </div>
                        </th>

                        <th className={thCls}>תשלום</th>

                        <th className={thCls} onClick={() => handleSort("attendance")}>
                          <div className="flex items-center gap-1.5">
                            {showAttendanceNow ? "נוכחות" : "סטטוס"}
                            <SortIcon field="attendance" sortConfig={sortConfig} />
                          </div>
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-50">
                      {sortedParticipants.map((p, idx) => {
                        const name =
                          p.fullName ||
                          p.name ||
                          `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
                          "—";

                        const attendanceLabel = getAttendanceLabel(p);

                        return (
                          <tr
                            key={p.id || idx}
                            className={`hover:bg-indigo-50/40 transition-colors ${
                              idx % 2 === 1 ? "bg-gray-50/30" : "bg-white"
                            }`}
                          >
                            <td className="px-4 py-3 text-xs text-gray-400 tabular-nums">
                              {idx + 1}
                            </td>

                            <td className="px-4 py-3">
                              <span className="font-semibold text-gray-800 text-sm">{name}</span>
                            </td>

                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className="text-gray-500 text-sm font-mono tracking-wide">
                                {p.phone || p.mobile || <span className="text-gray-300">—</span>}
                              </span>
                            </td>

                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className="text-gray-500 text-sm">
                                {p.email || <span className="text-gray-300">—</span>}
                              </span>
                            </td>

                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                שילם
                              </span>
                            </td>

                            <td className="px-4 py-3">
                              {!showAttendanceNow ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">
                                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                  טרם התחיל
                                </span>
                              ) : attendanceLabel === "נוכח" ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  נוכח
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-100">
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                                  נעדר
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  {sortedParticipants.length} מתוך {paidCount}
                </span>

                {searchTerm && (
                  <span className="flex items-center gap-1.5 text-xs text-indigo-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    תוצאות חיפוש
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                className="h-9 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}